import React, { useRef, useState } from 'react';
import {
    Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Snackbar,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import '../../styles/fonts/fonts.css';
import { useUploadProjectMutation, useUploadFirmMutation, useGetUserQuery } from 'api/api';
import FigmaModal from 'components/modal/FigmaModal';
import { getErrorMessage, getUploadRowErrors, UploadRowError } from 'lib/utils/getErrorMessage';

type UploadType = 'projects' | 'firms';

// Every other table in the app (ProjectTable's StyledTableCell) uses a solid dark header
// with white text, not a tint - a pale tint here read as a second, slightly-different-
// shade Alert bar stacked under the first, with no clear line between them. Solid
// `error.main` instead gives a header that's obviously its own section and still reads
// as "this table is about the error" rather than an ordinary grey data table.
const uploadErrorHeaderCellSx = {
    fontWeight: 'bold',
    py: 1,
    bgcolor: 'error.main',
    color: 'common.white',
};

// Matches StyledTableRow's zebra striping elsewhere in the app, so a long list of issues
// stays scannable instead of the rows blurring together.
const uploadErrorBodyRowSx = {
    '&:nth-of-type(even)': { bgcolor: '#E5E8EC' },
};

// Shared between the header table and the body table below so their columns line up -
// they're two separate <table> elements (see the comment where they're used), so nothing
// else forces that alignment.
const uploadErrorColumnWidths = ['28%', '10%', '62%'];

interface UploadButtonProps {
    type: UploadType;
    onSuccess?: VoidFunction;
}

const UploadButton = ({ type, onSuccess }: UploadButtonProps) => {
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [uploadProject] = useUploadProjectMutation();
    const [uploadFirm] = useUploadFirmMutation();
    const { data: userData } = useGetUserQuery()
    const [isModalOpen, setIsModalOpen] = useState(false);
    // The upload endpoint names the offending sheet and row, so the message is worth
    // showing verbatim. It replaced a fixed "contact a developer" string that discarded it.
    const [uploadError, setUploadError] = useState<string | null>(null);
    // A bulk-upload rejection can name dozens of rows - a table renders each as its own
    // row with no wrapping, instead of one long string crammed into a Snackbar.
    const [uploadRowErrors, setUploadRowErrors] = useState<UploadRowError[] | null>(null);

    // Valid file types for each upload type using MIME type as keys and extensions as values
    const validFileTypes = {
        // 'application/vnd.ms-excel': '.xls',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
        // 'application/vnd.ms-excel.sheet.binary.macroEnabled.12': '.xlsb',
        'application/vnd.ms-excel.sheet.macroEnabled.12': '.xlsm',
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) {
            const file = event.target.files[0];

            if (file) {
                // Checks if the file type is valid either by MIME type or extension
                const isValidType = Object.keys(validFileTypes).includes(file.type) ||
                    Object.values(validFileTypes).some(ext =>
                        file.name.toLowerCase().endsWith(ext.toLowerCase())
                    );

                if (!isValidType) {
                    setUploadError('Please upload a valid Excel file (.xlsm or .xlsx).');
                    setUploadRowErrors(null);
                    event.target.value = '';
                    return;
                }

                handleUpload(file);
            }

            // Clear the input so re-picking the same filename fires onChange again - after a
            // rejection the user fixes the workbook and re-selects it under the same name.
            event.target.value = '';
        }
    };

    const handleUpload = async (file: File, firm?: string) => {
        const formData = new FormData();
        formData.append('file', file);
        firm && formData.append('firm', firm);
        setUploadError(null);
        setUploadRowErrors(null);

        try {
            if (type === 'projects') {
                await uploadProject(formData).unwrap();
                if (onSuccess) {
                    onSuccess();
                }
            } else {
                await uploadFirm(formData).unwrap();
                if (onSuccess) {
                    onSuccess();
                }
            }
        } catch (error) {
            console.error('Error uploading file:', error);
            // A bulk-upload validation failure names every bad row/field - render that as
            // a table. Every other error shape (a single string) keeps the Snackbar.
            const rowErrors = getUploadRowErrors(error);
            if (rowErrors && rowErrors.length) {
                setUploadRowErrors(rowErrors);
            } else {
                setUploadError(getErrorMessage(error));
            }
        }
    }

    const handleButtonClick = () => {
        if (type === 'projects' && userData?.site_role === 'Super Admin') {
            setIsModalOpen(true);
        } else if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleModalResponse = (response: any) => {
        if (response.file && response.firm) {
            handleUpload(response.file, response.firm)
        } else if (response.file) {
            handleUpload(response.file)
        } else if (response !== 'Cancel')
            throw Error(
                "Modal response error"
            )
    }

    return (
        <>
            <FigmaModal open={isModalOpen} onClose={() => setIsModalOpen(false)} type={'uploadProject'} onResponse={handleModalResponse} />
            <div>
                <input
                    type="file"
                    accept={Object.values(validFileTypes).join(', ')}
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                />
                <Button
                    type="button"
                    onClick={handleButtonClick}
                    variant="outlined"
                    sx={{ mr: 2, borderRadius: 12 }}
                    startIcon={<img src={'/assets/icons/DownloadIcon.svg'} alt='Download Icon' />}
                >
                    {type === 'projects' ? 'Import Projects' : 'Import Firms'}
                </Button>
            </div>
            {/* No autoHideDuration: these messages name a spreadsheet row the user has to go
                and fix, so they stay until dismissed. */}
            <Snackbar
                open={Boolean(uploadError)}
                onClose={() => setUploadError(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert severity="error" onClose={() => setUploadError(null)} sx={{ maxWidth: 600 }}>
                    {uploadError}
                </Alert>
            </Snackbar>
            {/* Dozens of rows can fail at once - a table, not a Snackbar, so every row is
                its own <TableRow> and the list scrolls instead of pushing the page's own
                content around. Cells wrap normally: the table structure, not no-wrap
                styling, is what keeps one row's message from bleeding into the next. */}
            <Dialog
                open={Boolean(uploadRowErrors?.length)}
                onClose={() => setUploadRowErrors(null)}
                maxWidth="md"
                fullWidth
            >
                {/* Same severity="error" pink/red used by the Snackbar above and by
                    error banners elsewhere in the app, rather than a plain dialog title -
                    this is still an error, just with more of them to show at once. */}
                <DialogTitle sx={{ p: 0 }}>
                    <Alert severity="error" variant="standard" sx={{ borderRadius: 0 }}>
                        Upload rejected - {uploadRowErrors?.length} issue{uploadRowErrors?.length === 1 ? '' : 's'} found
                    </Alert>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 0 }}>
                    {/* The header is a separate table, entirely outside the scrolling
                        container below - not merely "sticky" inside it - so nothing about
                        scrolling (trackpad momentum, rubber-band overscroll, sticky-position
                        quirks with an ancestor's transform) can move it. `stickyHeader`
                        was dropped along with it. Matching fixed column widths on both
                        tables keep the two lined up. */}
                    <Table size="small" sx={{ tableLayout: 'fixed' }}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ ...uploadErrorHeaderCellSx, width: uploadErrorColumnWidths[0] }}>
                                    Sheet
                                </TableCell>
                                <TableCell sx={{ ...uploadErrorHeaderCellSx, width: uploadErrorColumnWidths[1] }}>
                                    Row
                                </TableCell>
                                <TableCell sx={{ ...uploadErrorHeaderCellSx, width: uploadErrorColumnWidths[2] }}>
                                    Issue
                                </TableCell>
                            </TableRow>
                        </TableHead>
                    </Table>
                    <TableContainer sx={{ maxHeight: 440, overflowY: 'auto' }}>
                        <Table size="small" sx={{ tableLayout: 'fixed' }}>
                            <TableBody>
                                {uploadRowErrors?.map((rowError, index) => (
                                    <TableRow key={index} sx={uploadErrorBodyRowSx}>
                                        <TableCell sx={{ width: uploadErrorColumnWidths[0] }}>{rowError.sheet}</TableCell>
                                        <TableCell sx={{ width: uploadErrorColumnWidths[1] }}>{rowError.row ?? '—'}</TableCell>
                                        <TableCell sx={{ width: uploadErrorColumnWidths[2] }}>{rowError.message}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions>
                    {/* Same outlined-pill treatment as the Import/Export buttons above,
                        rather than MUI's plain text-button default. */}
                    <Button variant="outlined" sx={{ borderRadius: 12 }} onClick={() => setUploadRowErrors(null)}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default UploadButton;